import { useCallback, useEffect, useRef, useState } from 'react';
import fetchAPI from '../utils/fetchAPI';

// Interfaces
interface SessionData {
  startTime: number;
  pageViews: number;
  mouseMovements: number;
  keystrokes: number;
  formInteractions: Array<{
    type: string;
    field: string;
    timestamp: number;
  }>;
  behaviorMetrics: Record<string, any>;
  typingPattern?: {
    keyTimes: number[];
    intervals: number[];
    backspaceCount: number;
  };
  windowFocusChanges?: number;
}

interface BehaviorData {
  sessionDuration: number;
  mouseMovements: number;
  keystrokes: number;
  typingSpeed: number | null;
  backspaceCount: number;
  formInteractions: number;
  humanLikeScore: number;
}

interface BrowserFeatures {
  webgl: boolean;
  webgl2: boolean;
  webrtc: boolean;
  indexeddb: boolean;
  websockets: boolean;
  serviceworker: boolean;
  pushnotifications: boolean;
  deviceorientation: boolean;
  touchevents: boolean;
  pointerevents: boolean;
  webassembly: boolean;
  es6modules: boolean;
  webworkers: boolean;
}

interface DeviceCapabilities {
  touchSupport: boolean;
  deviceMemory: number | null;
  hardwareConcurrency: number | null;
  connectionType: string | null;
  batterySupport: boolean;
  gamepadsSupport: boolean;
  mediaDevicesSupport: boolean;
  speechRecognitionSupport: boolean;
  notificationSupport: boolean;
  vibrationSupport: boolean;
}

interface DeviceFingerprint {
  userAgent: string;
  language: string;
  languages: readonly string[];
  platform: string;
  screenResolution: string;
  availableScreenResolution: string;
  colorDepth: number;
  pixelDepth: number;
  timezone: string;
  timezoneOffset: number;
  cookiesEnabled: boolean;
  doNotTrack: string | null;
  canvasFingerprint: string;
  webglVendor: string;
  plugins: string[];
  browserFeatures: BrowserFeatures;
  deviceCapabilities: DeviceCapabilities;
}

interface RiskAssessmentData {
  registrationId: string;
  timestamp: string;
  email: {
    address: string;
    domain: string;
  };
  device: {
    userAgent: string;
    screenResolution: string;
    colorDepth: number;
    timezone: string;
    language: string;
    fingerprintHash: string;
    cookiesEnabled: boolean;
    javascriptEnabled: boolean;
    plugins: string[];
    webglVendor: string;
    canvasFingerprint: string;
    browserFeatures: BrowserFeatures;
    deviceCapabilities: DeviceCapabilities;
  };
  registrationSession: {
    sessionId: string;
    duration: number;
    referrer: string;
    entryPoint: string;
    formInteractions: {
      fieldsFocused: string[];
      totalKeystrokes: number;
      backspaceCount: number;
      formCompletionTime: number;
    };
    mouseMovements: number;
    windowFocusChanges: number;
  };
  registrationBehavioral: {
    typingSpeedWpm: number | null;
    typingRhythmConsistent: boolean;
    errorCorrections: number;
    botLikePatterns: boolean;
    humanInteractionScore: number;
  };
  registrationData: {
    email: string;
    userName: string;
    registrationSource: string;
  };
}

interface FormData {
  email: string;
  userName: string;
  xsrfToken?: string;
}

interface RiskAssessmentResult {
  riskScore: number;
  riskLevel: 'low' | 'medium' | 'high';
  factors: {
    disposableEmail: boolean;
    deviceRisk: number;
    behaviorRisk: number;
    formRisk: number;
    emailValid: boolean;
    nameValid: boolean;
    dataConsistencyRisk?: number;
    serverDetectedBot?: boolean;
  };
  recommendation: 'allow' | 'verify' | 'block';
  timestamp: string;
}

interface UseUserRiskAssessmentOptions {
  /** Whether to automatically start tracking on mount */
  autoStart?: boolean;
}

interface UseUserRiskAssessmentReturn {
  /** Submit risk assessment data and get result */
  submitRiskAssessment: (formData: FormData) => Promise<RiskAssessmentResult>;
  /** Whether assessment is currently running */
  isAssessing: boolean;
  /** Last assessment result */
  lastResult: RiskAssessmentResult | null;
  /** Reset tracking data */
  reset: () => void;
}

/**
 * React hook for user risk assessment during registration
 */
export const useUserRiskAssessment = (options: UseUserRiskAssessmentOptions = {}): UseUserRiskAssessmentReturn => {
  const { autoStart = true } = options;

  const [isAssessing, setIsAssessing] = useState(false);
  const [lastResult, setLastResult] = useState<RiskAssessmentResult | null>(null);
  const sessionDataRef = useRef<SessionData>({
    startTime: Date.now(),
    pageViews: 0,
    mouseMovements: 0,
    keystrokes: 0,
    formInteractions: [],
    behaviorMetrics: {},
  });

  // Feature detection helper methods
  const hasWebGL = (): boolean => {
    try {
      const canvas = document.createElement('canvas');
      return !!(canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));
    } catch {
      return false;
    }
  };

  const hasWebGL2 = (): boolean => {
    try {
      const canvas = document.createElement('canvas');
      return !!canvas.getContext('webgl2');
    } catch {
      return false;
    }
  };

  const hasWebRTC = (): boolean => {
    const win = window as any;
    return !!(win.RTCPeerConnection || win.mozRTCPeerConnection || win.webkitRTCPeerConnection);
  };

  const hasIndexedDB = (): boolean => {
    return 'indexedDB' in window;
  };

  const hasWebSockets = (): boolean => {
    return 'WebSocket' in window;
  };

  const hasServiceWorker = (): boolean => {
    return 'serviceWorker' in navigator;
  };

  const hasPushNotifications = (): boolean => {
    return 'PushManager' in window;
  };

  const hasDeviceOrientation = (): boolean => {
    return 'DeviceOrientationEvent' in window;
  };

  const hasTouchEvents = (): boolean => {
    return 'ontouchstart' in window;
  };

  const hasPointerEvents = (): boolean => {
    return 'PointerEvent' in window;
  };

  const hasWebAssembly = (): boolean => {
    return 'WebAssembly' in window;
  };

  const hasES6Modules = (): boolean => {
    return 'import' in document.createElement('script');
  };

  const hasWebWorkers = (): boolean => {
    return 'Worker' in window;
  };

  // Utility functions
  const trackTypingBehavior = useCallback((event: KeyboardEvent) => {
    if (!sessionDataRef.current.typingPattern) {
      sessionDataRef.current.typingPattern = {
        keyTimes: [],
        intervals: [],
        backspaceCount: 0,
      };
    }

    const now = Date.now();
    sessionDataRef.current.typingPattern.keyTimes.push(now);

    if (event.key === 'Backspace') {
      sessionDataRef.current.typingPattern.backspaceCount++;
    }

    // Calculate typing intervals for rhythm analysis
    if (sessionDataRef.current.typingPattern.keyTimes.length > 1) {
      const interval =
        now - sessionDataRef.current.typingPattern.keyTimes[sessionDataRef.current.typingPattern.keyTimes.length - 2];
      sessionDataRef.current.typingPattern.intervals.push(interval);
    }
  }, []);

  const trackFormInteraction = useCallback((type: string, element: HTMLElement) => {
    const inputElement = element as HTMLInputElement;
    sessionDataRef.current.formInteractions.push({
      type: type,
      field: inputElement.name || inputElement.id || inputElement.type,
      timestamp: Date.now(),
    });
  }, []);

  const calculateTypingSpeed = useCallback((): number | null => {
    if (!sessionDataRef.current.typingPattern || sessionDataRef.current.typingPattern.keyTimes.length < 5) {
      return null;
    }

    const intervals = sessionDataRef.current.typingPattern.intervals;
    const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
    const wpm = Math.round(60000 / (avgInterval * 5)); // Assume 5 chars per word
    return Math.min(wpm, 200); // Cap at 200 WPM
  }, []);

  const calculateHumanScore = useCallback((): number => {
    let score = 50; // Start at neutral

    // Mouse movement indicates human behavior
    if (sessionDataRef.current.mouseMovements > 10) score += 20;
    if (sessionDataRef.current.mouseMovements > 50) score += 10;

    // Natural typing speed
    const wpm = calculateTypingSpeed();
    if (wpm && wpm > 20 && wpm < 120) score += 15;

    // Backspaces indicate human errors
    if (sessionDataRef.current.typingPattern?.backspaceCount && sessionDataRef.current.typingPattern.backspaceCount > 0)
      score += 10;

    // Form interaction patterns
    if (sessionDataRef.current.formInteractions.length > 2) score += 5;

    return Math.min(score, 100);
  }, [calculateTypingSpeed]);

  const getBrowserFeatures = useCallback((): BrowserFeatures => {
    return {
      webgl: hasWebGL(),
      webgl2: hasWebGL2(),
      webrtc: hasWebRTC(),
      indexeddb: hasIndexedDB(),
      websockets: hasWebSockets(),
      serviceworker: hasServiceWorker(),
      pushnotifications: hasPushNotifications(),
      deviceorientation: hasDeviceOrientation(),
      touchevents: hasTouchEvents(),
      pointerevents: hasPointerEvents(),
      webassembly: hasWebAssembly(),
      es6modules: hasES6Modules(),
      webworkers: hasWebWorkers(),
    };
  }, []);

  const getDeviceCapabilities = useCallback((): DeviceCapabilities => {
    const nav = navigator as any;
    return {
      touchSupport: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
      deviceMemory: nav.deviceMemory || null,
      hardwareConcurrency: navigator.hardwareConcurrency || null,
      connectionType: nav.connection?.effectiveType || null,
      batterySupport: 'getBattery' in navigator,
      gamepadsSupport: 'getGamepads' in navigator,
      mediaDevicesSupport: 'mediaDevices' in navigator,
      speechRecognitionSupport: 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window,
      notificationSupport: 'Notification' in window,
      vibrationSupport: 'vibrate' in navigator,
    };
  }, []);

  const getWebGLVendor = useCallback((): string => {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      if (gl && 'getExtension' in gl) {
        const debugInfo = (gl as WebGLRenderingContext).getExtension('WEBGL_debug_renderer_info');
        return debugInfo ? (gl as WebGLRenderingContext).getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : 'Unknown';
      }
    } catch {
      return 'Not supported';
    }
    return 'Unknown';
  }, []);

  const getPlugins = useCallback((): string[] => {
    const plugins: string[] = [];
    for (let i = 0; i < navigator.plugins.length; i++) {
      plugins.push(navigator.plugins[i].name);
    }
    return plugins.slice(0, 10); // Limit to first 10 plugins
  }, []);

  const getDeviceFingerprint = useCallback((): DeviceFingerprint => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Device fingerprint text', 2, 2);
    }
    const canvasFingerprint = canvas.toDataURL().slice(-50);

    const browserFeatures = getBrowserFeatures();
    const deviceCapabilities = getDeviceCapabilities();

    return {
      userAgent: navigator.userAgent,
      language: navigator.language,
      languages: navigator.languages,
      platform: navigator.platform,
      screenResolution: `${screen.width}x${screen.height}`,
      availableScreenResolution: `${screen.availWidth}x${screen.availHeight}`,
      colorDepth: screen.colorDepth,
      pixelDepth: screen.pixelDepth,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timezoneOffset: new Date().getTimezoneOffset(),
      cookiesEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack,
      canvasFingerprint: canvasFingerprint,
      webglVendor: getWebGLVendor(),
      plugins: getPlugins(),
      browserFeatures: browserFeatures,
      deviceCapabilities: deviceCapabilities,
    };
  }, [getBrowserFeatures, getDeviceCapabilities, getWebGLVendor, getPlugins]);

  const analyzeBehavior = useCallback((): BehaviorData => {
    const now = Date.now();
    const sessionDuration = (now - sessionDataRef.current.startTime) / 1000;

    return {
      sessionDuration: sessionDuration,
      mouseMovements: sessionDataRef.current.mouseMovements,
      keystrokes: sessionDataRef.current.keystrokes,
      typingSpeed: calculateTypingSpeed(),
      backspaceCount: sessionDataRef.current.typingPattern?.backspaceCount || 0,
      formInteractions: sessionDataRef.current.formInteractions.length,
      humanLikeScore: calculateHumanScore(),
    };
  }, [calculateTypingSpeed, calculateHumanScore]);

  const generateFingerprint = useCallback((deviceData: DeviceFingerprint): string => {
    const string = JSON.stringify(deviceData);
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
      const char = string.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return `fp_${Math.abs(hash).toString(36)}`;
  }, []);

  const analyzeTypingRhythm = useCallback((): boolean => {
    if (!sessionDataRef.current.typingPattern?.intervals || sessionDataRef.current.typingPattern.intervals.length < 5) {
      return true; // Assume consistent if not enough data
    }

    const intervals = sessionDataRef.current.typingPattern.intervals;
    const avg = intervals.reduce((a, b) => a + b, 0) / intervals.length;
    const variance = intervals.reduce((sum, interval) => sum + Math.pow(interval - avg, 2), 0) / intervals.length;
    const stdDev = Math.sqrt(variance);

    // Low standard deviation indicates bot-like consistent timing
    return stdDev > 50; // Humans have natural variation
  }, []);

  const getRegistrationSource = useCallback((): string => {
    const ref = document.referrer;
    if (!ref) return 'direct';
    if (ref.includes('google.com')) return 'organic_search';
    if (ref.includes('facebook.com') || ref.includes('twitter.com')) return 'social_media';
    return 'referral';
  }, []);

  // Initialize event listeners
  useEffect(() => {
    if (!autoStart) return;

    const handleMouseMove = () => {
      sessionDataRef.current.mouseMovements++;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      sessionDataRef.current.keystrokes++;
      trackTypingBehavior(e);
    };

    const handleFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        trackFormInteraction('focus', target);
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        sessionDataRef.current.windowFocusChanges = (sessionDataRef.current.windowFocusChanges || 0) + 1;
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('focus', handleFocus, true);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('focus', handleFocus, true);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [autoStart, trackTypingBehavior, trackFormInteraction]);

  const submitRiskAssessment = useCallback(
    async (formData: FormData): Promise<RiskAssessmentResult> => {
      setIsAssessing(true);

      try {
        const deviceData = getDeviceFingerprint();
        const behaviorData = analyzeBehavior();
        const now = new Date();

        const riskAssessmentData: RiskAssessmentData = {
          registrationId: `reg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          timestamp: now.toISOString(),

          email: {
            address: formData.email,
            domain: formData.email.split('@')[1],
          },

          device: {
            userAgent: deviceData.userAgent,
            screenResolution: deviceData.screenResolution,
            colorDepth: deviceData.colorDepth,
            timezone: deviceData.timezone,
            language: deviceData.language,
            fingerprintHash: generateFingerprint(deviceData),
            cookiesEnabled: deviceData.cookiesEnabled,
            javascriptEnabled: true,
            plugins: deviceData.plugins,
            webglVendor: deviceData.webglVendor,
            canvasFingerprint: deviceData.canvasFingerprint,
            browserFeatures: deviceData.browserFeatures,
            deviceCapabilities: deviceData.deviceCapabilities,
          },

          registrationSession: {
            sessionId: `sess_reg_${Date.now()}`,
            duration: behaviorData.sessionDuration,
            referrer: document.referrer,
            entryPoint: window.location.pathname,
            formInteractions: {
              fieldsFocused: sessionDataRef.current.formInteractions.map((i) => i.field),
              totalKeystrokes: behaviorData.keystrokes,
              backspaceCount: behaviorData.backspaceCount,
              formCompletionTime: behaviorData.sessionDuration,
            },
            mouseMovements: behaviorData.mouseMovements,
            windowFocusChanges: sessionDataRef.current.windowFocusChanges || 0,
          },

          registrationBehavioral: {
            typingSpeedWpm: behaviorData.typingSpeed,
            typingRhythmConsistent: analyzeTypingRhythm(),
            errorCorrections: behaviorData.backspaceCount,
            botLikePatterns: behaviorData.humanLikeScore < 30,
            humanInteractionScore: behaviorData.humanLikeScore,
          },

          registrationData: {
            email: formData.email,
            userName: formData.userName,
            registrationSource: getRegistrationSource(),
          },
        };

        // Send data to server for assessment
        const assessmentUrl = '/identity/v2/new-user';

        const response = await fetchAPI({
          method: 'POST',
          url: assessmentUrl,
          xsrfToken: formData.xsrfToken,
          payload: riskAssessmentData,
        });

        if (!response.ok) {
          // be silent on errors to avoid disrupting user flow
          return Promise.resolve(null as any);
        }

        const result = await response.json();
        setLastResult(result);
        return result;
      } catch {
        // be silent on errors to avoid disrupting user flow
        return Promise.resolve(null as any);
      } finally {
        setIsAssessing(false);
      }
    },
    [getDeviceFingerprint, analyzeBehavior, generateFingerprint, analyzeTypingRhythm, getRegistrationSource],
  );

  const reset = useCallback(() => {
    sessionDataRef.current = {
      startTime: Date.now(),
      pageViews: 0,
      mouseMovements: 0,
      keystrokes: 0,
      formInteractions: [],
      behaviorMetrics: {},
    };
    setLastResult(null);
  }, []);

  return {
    submitRiskAssessment,
    isAssessing,
    lastResult,
    reset,
  };
};

export default useUserRiskAssessment;
